import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Eye,
  ExternalLink,
  Settings,
  Store,
  Phone,
  MapPin,
  Clock,
  Instagram,
  Facebook,
  Loader2,
  Save,
  Upload,
  Image as ImageIcon,
  Smartphone,
  Monitor,
  RefreshCw,
  Package,
  CheckCircle,
  AlertCircle,
  Link2,
  Palette,
  Globe
} from 'lucide-react';

const MenuAdmin = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('mobile'); // mobile or desktop
  const [previewKey, setPreviewKey] = useState(0); // For refreshing iframe
  
  // Business Info
  const [businessInfo, setBusinessInfo] = useState(null);
  
  // Stats from POS
  const [posStats, setPosStats] = useState({
    totalProducts: 0,
    visibleProducts: 0,
    categories: 0,
    popularProducts: 0
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const MENU_URL = `${window.location.origin}/menu`;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch business info
      const bizRes = await api.get('/menu-public/admin/business-info');
      setBusinessInfo(bizRes.data);
      
      // Fetch POS stats
      const landingRes = await api.get('/menu-public/landing/mr-frogs');
      setPosStats({
        totalProducts: landingRes.data.products?.length || 0,
        visibleProducts: landingRes.data.products?.filter(p => p.is_available !== false).length || 0,
        categories: landingRes.data.categories?.length || 0,
        popularProducts: landingRes.data.products?.filter(p => p.is_popular).length || 0
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBusinessInfo = async () => {
    setSaving(true);
    try {
      const res = await api.put('/menu-public/admin/business-info', businessInfo);
      setBusinessInfo(res.data);
      toast.success('✅ Configuración guardada');
      // Refresh preview
      setPreviewKey(prev => prev + 1);
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBusinessInfo({ ...businessInfo, [field]: res.data.url });
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const refreshPreview = () => {
    setPreviewKey(prev => prev + 1);
    toast.success('Vista previa actualizada');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Globe className="w-6 h-6 text-blue-500" />
                Menú Digital - Vista Previa
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Visualiza cómo se ve tu menú público • Los productos se administran desde el <a href="/products" className="text-blue-500 hover:underline font-medium">Catálogo</a>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={refreshPreview}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
              <Button 
                onClick={() => window.open(MENU_URL, '_blank')}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Abrir Menú Público
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Productos Visibles</p>
                  <p className="text-3xl font-bold">{posStats.visibleProducts}</p>
                </div>
                <Package className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Categorías</p>
                  <p className="text-3xl font-bold">{posStats.categories}</p>
                </div>
                <Store className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Populares ⭐</p>
                  <p className="text-3xl font-bold">{posStats.popularProducts}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Total Catálogo</p>
                  <p className="text-3xl font-bold">{posStats.totalProducts}</p>
                </div>
                <Globe className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-blue-800 font-medium">Sistema Unificado</p>
            <p className="text-blue-600 text-sm">
              Los productos del menú digital vienen directamente de tu <strong>Catálogo POS</strong>. 
              Para agregar o editar productos, ve a <a href="/products" className="underline font-medium">Catálogo → Productos</a> y activa "Visible en Menú Digital".
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Configuration */}
          <div className="lg:col-span-1 space-y-6">
            {/* Business Config Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Settings className="w-5 h-5" />
                  Configuración del Negocio
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo */}
                <div>
                  <Label className="text-sm font-medium">Logo</Label>
                  <div className="mt-2 flex items-center gap-4">
                    {businessInfo?.logo_url ? (
                      <img src={businessInfo.logo_url} alt="Logo" className="w-16 h-16 rounded-lg object-cover border" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border">
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, 'logo_url')}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                          Cambiar
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>

                {/* Business Name */}
                <div>
                  <Label className="text-sm font-medium">Nombre del Negocio</Label>
                  <Input
                    value={businessInfo?.business_name || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, business_name: e.target.value })}
                    placeholder="Mi Restaurante"
                    className="mt-1"
                  />
                </div>

                {/* Slogan */}
                <div>
                  <Label className="text-sm font-medium">Slogan</Label>
                  <Input
                    value={businessInfo?.slogan || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, slogan: e.target.value })}
                    placeholder="Los mejores platillos..."
                    className="mt-1"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Teléfono
                  </Label>
                  <Input
                    value={businessInfo?.phone || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
                    placeholder="622 123 4567"
                    className="mt-1"
                  />
                </div>

                {/* Address */}
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Dirección
                  </Label>
                  <Input
                    value={businessInfo?.address || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, address: e.target.value })}
                    placeholder="Calle Principal #123"
                    className="mt-1"
                  />
                </div>

                {/* Schedule */}
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Horario
                  </Label>
                  <Input
                    value={businessInfo?.schedule || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, schedule: e.target.value })}
                    placeholder="Lun-Dom: 10am - 10pm"
                    className="mt-1"
                  />
                </div>

                {/* Social Media */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-1">
                      <Instagram className="w-3 h-3" /> Instagram
                    </Label>
                    <Input
                      value={businessInfo?.instagram || ''}
                      onChange={(e) => setBusinessInfo({ ...businessInfo, instagram: e.target.value })}
                      placeholder="@usuario"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-1">
                      <Facebook className="w-3 h-3" /> Facebook
                    </Label>
                    <Input
                      value={businessInfo?.facebook || ''}
                      onChange={(e) => setBusinessInfo({ ...businessInfo, facebook: e.target.value })}
                      placeholder="MiNegocio"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* WhatsApp */}
                <div>
                  <Label className="text-sm font-medium">WhatsApp para Pedidos</Label>
                  <Input
                    value={businessInfo?.whatsapp || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, whatsapp: e.target.value })}
                    placeholder="526221234567"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Incluye código de país (52 para México)</p>
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleSaveBusinessInfo} 
                  className="w-full bg-green-500 hover:bg-green-600"
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar Cambios
                </Button>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Link2 className="w-5 h-5" />
                  Enlaces Rápidos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a 
                  href="/products" 
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Catálogo de Productos</p>
                    <p className="text-xs text-gray-500">Agregar y editar productos</p>
                  </div>
                </a>
                <a 
                  href="/customers" 
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Store className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Clientes VIP</p>
                    <p className="text-xs text-gray-500">Ver clientes y puntos</p>
                  </div>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Eye className="w-5 h-5" />
                    Vista Previa en Vivo
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {/* Device Toggle */}
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setPreviewMode('mobile')}
                        className={`p-2 rounded-md transition-colors ${previewMode === 'mobile' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        <Smartphone className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setPreviewMode('desktop')}
                        className={`p-2 rounded-md transition-colors ${previewMode === 'desktop' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        <Monitor className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 flex justify-center bg-gray-100 min-h-[600px]">
                {/* Preview Frame */}
                <div className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 ${
                  previewMode === 'mobile' 
                    ? 'w-[375px] h-[700px]' 
                    : 'w-full max-w-4xl h-[600px]'
                }`}>
                  {/* Phone Frame for Mobile */}
                  {previewMode === 'mobile' && (
                    <div className="bg-black h-6 flex items-center justify-center">
                      <div className="w-20 h-4 bg-black rounded-full"></div>
                    </div>
                  )}
                  {/* iframe */}
                  <iframe
                    key={previewKey}
                    src={MENU_URL}
                    className="w-full h-full border-0"
                    title="Vista previa del menú"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuAdmin;
